// Aktionen und Abschriften: zeitlich begrenzte Preise mit Namen, wahlweise nur an einzelnen Standorten.
//
//   vorschlagen (propose)  Filiale für ihre Standorte – ändert keinen Preis, wartet auf Freigabe
//   anlegen     (create)   für alle Standorte; wirksam nur, wenn die Person selbst freigeben darf, sonst Vorschlag
//   Abschrift   (markdown) an EINEM Standort direkt wirksam – bis zur Grenze `maxDiscountBp` der Rolle
//   freigeben / ablehnen   zweite Person; erst die Freigabe schreibt die Aktionsliste, die die Preisfindung kennt
//   beenden                eine freigegebene Aktion vorzeitig beenden
import { PERMISSION_COSTS_READ, defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { listInput, listOutput } from '@shared/list'
import { z } from 'zod'
import { zCampaignAnnounce, zCampaignBrief } from './campaigns'
import { zBasisPoints, zById, zDay, zLocationRef, zMarginInfo, zOptionalText, zPerson, zPriceCents, zPriceMode, zReason, zText, zUnitRef } from './common'

const READ = 'pricing.promotion.read'

export const PROMOTION_KINDS = ['promotion', 'markdown'] as const
export type PromotionKind = (typeof PROMOTION_KINDS)[number]

/**
 * Zustand einer Aktion, wie die Oberfläche ihn zeigt: proposed = wartet auf Freigabe · approved = freigegeben,
 * beginnt noch · running = läuft heute · ended = Zeitraum vorbei oder vorzeitig beendet · rejected = abgelehnt ·
 * withdrawn = zurückgezogen.
 */
export const PROMOTION_STATES = ['proposed', 'approved', 'running', 'ended', 'rejected', 'withdrawn'] as const
export type PromotionState = (typeof PROMOTION_STATES)[number]

const zPromotionLineInput = z
  .strictObject({
    itemId: zId,
    /** Gebinde (item_unit.id); ohne Angabe das Verkaufsgebinde des Artikels */
    unitId: zId.nullish(),
    /** Aktionspreis in Cent je Gebinde in der Basis der Preisgruppe – ODER `discountBp` */
    amountCents: zPriceCents.optional(),
    /** Nachlass in Basispunkten auf den regulären Preis (2000 = 20 %) – ODER `amountCents` */
    discountBp: zBasisPoints.optional(),
  })
  .refine((line) => (line.amountCents === undefined) !== (line.discountBp === undefined), {
    error: 'Bitte je Artikel entweder den Aktionspreis oder den Nachlass in Prozent angeben.',
    path: ['amountCents'],
  })

const zPromotionLines = z.array(zPromotionLineInput).min(1, { error: 'Bitte mindestens einen Artikel in die Aktion aufnehmen.' }).max(300)

const promotionShape = {
  name: zText(120),
  validFrom: zDay,
  validTo: zDay,
  /** Preisgruppe, für die die Aktion gilt; ohne Angabe die Standard-Preisgruppe (Filialpreis) */
  priceGroupId: zId.nullish(),
  reason: zOptionalText(500),
  lines: zPromotionLines,
}

export const zPromotionRow = z.object({
  id: zId,
  kind: z.enum(PROMOTION_KINDS),
  name: z.string(),
  state: z.enum(PROMOTION_STATES),
  validFrom: z.string(),
  validTo: z.string(),
  /** „13.–19.10.“ */
  period: z.string(),
  priceMode: zPriceMode,
  priceGroup: z.object({ id: zId, name: z.string() }).nullable(),
  /** leer = alle Standorte */
  locations: z.array(zLocationRef),
  itemCount: z.int(),
  reason: z.string().nullable(),
  proposedBy: zPerson,
  proposedAt: z.date(),
  decidedBy: zPerson.nullable(),
  decidedAt: z.date().nullable(),
  decisionNote: z.string().nullable(),
  /** ohne zweite Person wirksam geworden (eigenes Freigaberecht bzw. Abschrift innerhalb der Grenze) */
  direct: z.boolean(),
})
export type PromotionRow = z.output<typeof zPromotionRow>

export const promotionList = defineContract({
  name: 'pricing.promotion.list',
  title: 'Aktionen anzeigen',
  description: 'Aktionen und Abschriften mit Zeitraum, Standorten und Zustand – auch Vorschläge, die auf Freigabe warten.',
  llm: {
    description:
      'Listet Aktionen und Abschriften: Name, Zeitraum, Standorte (leer = alle), Zahl der Artikel und Zustand ' +
      '(proposed = wartet auf Freigabe, approved = freigegeben und noch nicht begonnen, running = läuft, ended, ' +
      'rejected, withdrawn). Filter `state`, `open` (nur vorgeschlagene, freigegebene und laufende), `locationId`, `kind`. ' +
      'Einzelheiten und Preise: pricing.promotion.get.',
    keywords: ['Aktion', 'Aktionen', 'Angebot der Woche', 'Abschrift', 'Abverkauf', 'laufende Aktionen', 'Vorschlag'],
  },
  input: listInput({
    sortBy: ['validFrom', 'name'],
    defaultSort: { by: 'validFrom', dir: 'desc' },
    filters: {
      state: z.enum(PROMOTION_STATES).optional(),
      /** nur was noch zählt: vorgeschlagen, freigegeben oder laufend */
      open: z.boolean().optional(),
      kind: z.enum(PROMOTION_KINDS).optional(),
      locationId: zId.optional(),
    },
  }),
  output: listOutput(zPromotionRow),
  permission: READ,
  risk: 'read',
})

export const zPromotionLine = z.object({
  itemUnitId: zId,
  itemId: zId,
  sku: z.string(),
  name: z.string(),
  unit: zUnitRef,
  /** Aktionspreis je Gebinde in der Basis der Aktion */
  amountCents: z.int(),
  pricePerQty: z.int(),
  /** regulärer Preis beim Vorschlag; null = es gab keinen */
  listCents: z.int().nullable(),
  /** Nachlass gegenüber dem regulären Preis in Basispunkten */
  discountBp: z.int().nullable(),
  /** Grundpreis des Aktionspreises: „1,58 €/l“ */
  basePriceText: z.string().nullable(),
  margin: zMarginInfo.optional(),
})

export const promotionGet = defineContract({
  name: 'pricing.promotion.get',
  title: 'Aktion öffnen',
  description: 'Zeigt eine Aktion mit Artikeln, Aktionspreisen, Nachlass, Standorten und dem Stand der Freigabe.',
  llm: {
    description:
      'Liefert eine Aktion oder Abschrift mit allen Artikeln (Aktionspreis, regulärer Preis, Nachlass), Zeitraum, ' +
      'Standorten, wer vorgeschlagen und wer entschieden hat – und ob die Person sie freigeben, zurückziehen oder ' +
      'beenden darf. Spannen nur mit „Einstandspreise sehen“.',
    keywords: ['Aktion öffnen', 'Aktionspreise', 'Vorschlag prüfen'],
  },
  input: zById,
  output: zPromotionRow.extend({
    lines: z.array(zPromotionLine),
    /** die Charge, um die es bei einer Abschrift geht */
    lot: z.object({ id: zId, lotNo: z.string(), bestBefore: z.string().nullable() }).nullable(),
    /** Preislisten, die aus der freigegebenen Aktion entstanden sind */
    priceListIds: z.array(zId),
    canApprove: z.boolean(),
    /** warum die Person nicht freigeben darf (eigener Vorschlag) */
    approveHint: z.string().nullable(),
    canWithdraw: z.boolean(),
    canEnd: z.boolean(),
  }),
  permission: READ,
  risk: 'read',
  fieldPermissions: { 'lines.*.margin': PERMISSION_COSTS_READ },
})

/** approved = sofort wirksam (im Zeitraum) · proposed = wartet auf die Freigabe einer zweiten Person */
export const PROMOTION_OUTCOMES = ['approved', 'proposed'] as const

/** Ankündigung im Vertriebskanal, die mit der Aktion entstanden ist (`announce`) */
const zAnnounced = { campaign: zCampaignBrief.optional() }

const zPromotionResult = z.object({ id: zId, outcome: z.enum(PROMOTION_OUTCOMES), state: z.enum(PROMOTION_STATES), message: z.string(), labelCount: z.int(), ...zAnnounced })

/**
 * „In der Bestell-App ankündigen“ im selben Vorgang: Ohne diese Angabe entsteht keine Werbe-Mitteilung. Verlangt das
 * Recht pricing.campaign.write und einen angebundenen Kanal (pricing.campaign.create).
 */
const zAnnounceOption = { announce: zCampaignAnnounce.optional() }

export const promotionPropose = defineContract({
  name: 'pricing.promotion.propose',
  title: 'Aktion vorschlagen',
  description: 'Schlägt eine Aktion für eigene Standorte vor. Es ändert sich kein Preis, bis eine zweite Person freigibt.',
  llm: {
    description:
      'Stellt einen Aktionsvorschlag für die genannten Standorte (`locationIds`, mindestens einer – die Person braucht ' +
      'das Vorschlagsrecht an jedem): Name, Zeitraum, je Artikel entweder Aktionspreis (`amountCents`, Basis der ' +
      'Preisgruppe) oder Nachlass (`discountBp`). Es ändert sich KEIN Preis; die Freigabe erteilt eine zweite Person ' +
      'mit pricing.promotion.approve. Verwenden, wenn jemand ohne Preisrecht eine Aktion oder einen Abverkauf ' +
      'möchte. `lotId` grenzt einen Abverkauf auf einen Warenposten ein (nur Betriebe mit Rückverfolgung).',
    keywords: ['Aktion vorschlagen', 'Aktionsvorschlag', 'Abverkauf vorschlagen', 'Preis senken'],
  },
  input: z.strictObject({
    ...promotionShape,
    locationIds: z.array(zId).min(1, { error: 'Bitte mindestens einen Standort wählen.' }).max(50),
    lotId: zId.nullish(),
  }),
  output: zPromotionResult,
  permission: 'pricing.promotion.propose',
  risk: 'write',
  scope: (input) => input.locationIds,
})

export const promotionCreate = defineContract({
  name: 'pricing.promotion.create',
  title: 'Aktion anlegen',
  description: 'Legt eine Aktion für alle oder einzelne Standorte an. Ohne eigenes Freigaberecht wartet sie auf Freigabe.',
  llm: {
    description:
      'Legt eine Aktion an: Name, Zeitraum (`validFrom` bis `validTo`, beide Tage eingeschlossen), Standorte ' +
      '(`locationIds` leer = alle), je Artikel Aktionspreis oder Nachlass. Hält die Person das Freigaberecht für ' +
      'Aktionen, ist die Aktion sofort freigegeben (`outcome: approved`) und gilt ab `validFrom`; sonst wartet sie ' +
      'als Vorschlag auf eine zweite Person (`outcome: proposed`). Für Filialen ohne Preisrecht: ' +
      'pricing.promotion.propose. Dauerhafte Preisänderungen: pricing.price.set. `announce` kündigt die Aktion im ' +
      'selben Schritt in der Bestell-App als Werbe-Mitteilung an (Felder wie pricing.campaign.create; nur wenn die ' +
      'Person es ausdrücklich möchte).',
    keywords: ['Aktion anlegen', 'Angebot der Woche', 'Sonderangebot', 'Aktionspreis', 'Wochenangebot'],
  },
  input: z.strictObject({ ...promotionShape, locationIds: z.array(zId).max(50).default([]), ...zAnnounceOption }),
  output: zPromotionResult,
  permission: 'pricing.promotion.write',
  risk: 'financial',
})

export const markdownCreate = defineContract({
  name: 'pricing.markdown.create',
  title: 'Abschrift anlegen',
  description: 'Senkt Preise an einem Standort für einen Abverkauf – direkt wirksam bis zur Grenze der Rolle.',
  llm: {
    description:
      'Legt eine Abschrift an EINEM Standort an (Abverkauf, Restposten): Zeitraum und je Artikel Aktionspreis oder ' +
      'Nachlass. Sie ist sofort wirksam, wenn jeder Nachlass innerhalb der Grenze der Rolle liegt (`maxDiscountBp`); ' +
      'liegt einer darüber, wird die Aktion abgelehnt und nennt die Grenze – dann pricing.promotion.propose ' +
      'verwenden. Wer das Recht nicht hat, schlägt ebenfalls nur vor.',
    keywords: ['Abschrift', 'reduzieren', 'Preis senken', 'Restposten', 'abverkaufen'],
  },
  input: z.strictObject({ ...promotionShape, locationId: zId, lotId: zId.nullish() }),
  output: zPromotionResult,
  permission: 'pricing.markdown.create',
  risk: 'financial',
  scope: (input) => [input.locationId],
})

const zPromotionDecision = z.object({ id: zId, state: z.enum(PROMOTION_STATES), message: z.string(), labelCount: z.int(), ...zAnnounced })

export const promotionApprove = defineContract({
  name: 'pricing.promotion.approve',
  title: 'Aktion freigeben',
  description: 'Gibt einen Aktionsvorschlag frei: Die Aktionspreise gelten im Zeitraum an den genannten Standorten.',
  llm: {
    description:
      'Gibt einen Aktionsvorschlag frei. Erst damit entsteht die Aktionsliste, die die Preisfindung kennt – im ' +
      'Zeitraum und nur an den Standorten der Aktion. Niemand gibt den eigenen Vorschlag frei (Vier-Augen). Zum ' +
      'Ablehnen pricing.promotion.reject. `announce` kündigt die Aktion mit der Freigabe in der Bestell-App an.',
    keywords: ['Aktion freigeben', 'Vorschlag freigeben', 'genehmigen', 'Abverkauf freigeben'],
  },
  input: z.strictObject({ id: zId, note: zOptionalText(500), ...zAnnounceOption }),
  output: zPromotionDecision,
  permission: 'pricing.promotion.approve',
  risk: 'financial',
})

export const promotionReject = defineContract({
  name: 'pricing.promotion.reject',
  title: 'Aktion ablehnen',
  description: 'Lehnt einen Aktionsvorschlag mit Grund ab. Kein Preis ändert sich.',
  llm: {
    description: 'Lehnt einen Aktionsvorschlag mit Grund ab; die vorschlagende Person wird benachrichtigt. Kein Preis ändert sich.',
    keywords: ['Aktion ablehnen', 'Vorschlag ablehnen'],
  },
  input: z.strictObject({ id: zId, reason: zReason }),
  output: zPromotionDecision,
  permission: 'pricing.promotion.approve',
  risk: 'write',
})

export const promotionWithdraw = defineContract({
  name: 'pricing.promotion.withdraw',
  title: 'Aktionsvorschlag zurückziehen',
  description: 'Zieht einen eigenen Vorschlag zurück, solange niemand entschieden hat.',
  llm: {
    description:
      'Zieht einen Aktionsvorschlag zurück, solange er auf Freigabe wartet. Das darf, wer ihn gestellt hat, oder wer ' +
      'Aktionen pflegt. Kein Preis ändert sich.',
    keywords: ['Vorschlag zurückziehen', 'Aktion verwerfen'],
  },
  input: zById,
  output: zPromotionDecision,
  permission: READ,
  risk: 'write',
})

export const promotionEnd = defineContract({
  name: 'pricing.promotion.end',
  title: 'Aktion vorzeitig beenden',
  description: 'Beendet eine freigegebene Aktion: Ab morgen – oder sofort – gelten wieder die regulären Preise.',
  llm: {
    description:
      'Beendet eine freigegebene Aktion vorzeitig. `immediately: true` nimmt die Aktionspreise sofort aus der ' +
      'Preisfindung, sonst gilt die Aktion noch bis Ende des heutigen Tages. Eine noch nicht begonnene Aktion wird ' +
      'damit abgesagt. Das dürfen Personen mit dem Recht für Aktionen, eine Abschrift auch, wer sie angelegt hat.',
    keywords: ['Aktion beenden', 'Aktion stoppen', 'Aktion absagen', 'Abverkauf beenden'],
  },
  input: z.strictObject({ id: zId, immediately: z.boolean().default(false), reason: zOptionalText(500) }),
  output: zPromotionDecision,
  permission: READ,
  risk: 'financial',
})

export const promotionAdopt = defineContract({
  name: 'pricing.promotion.adopt',
  title: 'Aktionsliste als Aktion übernehmen',
  description: 'Führt eine vorhandene Aktionsliste (aus Import oder Altbestand) als freigegebene Aktion.',
  llm: {
    description:
      'Übernimmt eine vorhandene Aktionsliste ohne Vorgang (aus einem Import oder der Datenübernahme) als ' +
      'freigegebene Aktion, damit sie unter „Aktionen“ erscheint und sich beenden lässt. Ändert keinen Preis. ' +
      '`announce` kündigt sie zugleich in der Bestell-App an.',
    keywords: ['Aktionsliste übernehmen'],
  },
  input: z.strictObject({ priceListId: zId, ...zAnnounceOption }),
  output: z.object({ id: zId, created: z.boolean(), ...zAnnounced }),
  permission: 'pricing.promotion.approve',
  risk: 'write',
  assistant: 'opt-in',
})
